import React, { useState, useEffect } from "react";

import AuthPage from "./pages/AuthPage";
import { useAuthContext } from "./context/AuthContext";
import { useCognitoContext } from "./context/CognitoContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Router from "./components/Router";
import UpdateModal from "./components/UpdateModal/UpdateModal";

import useIdleTimer from "./hooks/useIdleTimer";
import useActivityMonitor from "./hooks/useActivityMonitor";
import CssBaseline from '@mui/material/CssBaseline';
import PageSpinner from "./components/PageSpinner";
import { useAppContext } from "./context/appContext";
import BasicSnackbar from "./components/common/BasicSnackbar/BasicSnackbar";
import { ThemeProvider } from "./context/ThemeContext";
import { ScreenResolutionProvider } from "./context/screenResolutionContext";
import UpdateWarningModal from "./components/UpdateWarningModal/UpdateWarningModal";
import { CompatibilityMatrixProvider } from "./context/compatibilityMatrixContext";
import UpdateInstalledModal from "./components/UpdateInstalledModal/UpdateInstalledModal";
import useInitializeBackendStatus from "./hooks/useInitializeBackendStatus";
import useRebootMonitor from "./hooks/useRebootMonitor";


// eslint-disable-next-line no-undef
const serverUri = import.meta.env.VITE_APP_SERVER_URI;
// eslint-disable-next-line no-undef
const apiVersion = import.meta.env.VITE_APP_AUTH_SERVICE_API_VERSION;

export default function App() {
	const { isAuthenticated, setIsAuthenticated, userData, setUserData } = useAuthContext();
	const { getAuthenticatedUser, signOut } = useCognitoContext();
	const [isLoading, setIsLoading] = useState(true); // New loading state
	const { isAcquireRunning } = useAppContext();

	const isTester = userData && userData.groups && userData.groups.includes("Reyedar-tester");
	const backendAuthenticated = localStorage.getItem("isBackendAuthenticated") === "true";
	const shouldInitializeBackendStatus = isAuthenticated && backendAuthenticated && !isTester; 

	const onIdle = () => {
		// If the user is authenticated and no data acquisition is running, sign out due to inactivity
		if (isAuthenticated && !isAcquireRunning) {
			setIsAuthenticated(false);
			signOut();
		}
	};

	const keepAliveAction = async () => {
		// Skip keep-alive action if data acquisition is running
		if (isAcquireRunning) {
			return;
		}
		try {
			const response = await fetch(
				`${serverUri}/${apiVersion}/keep-alive`,
				{
					method: "POST",
					credentials: "include",
					headers: {
						"Content-Type": "application/json; charset=utf-8",
						"Cache-Control": "no-cache, no-store, must-revalidate",
						"X-CSRF-Token": localStorage.getItem("csrfToken"),
					},
				}
			);

			if (!response.ok) {
				const errorResponse = await response.json(); // Assuming error details are also sent as JSON
				throw new Error(`Error: ${errorResponse.message || response.statusText}`);
			}
		} catch (error) {
			console.error(error.message);
		}
	};

	const idleDuration = 60 * 60 * 1000;
	const keepAliveInterval = 10 * 1000;

	useIdleTimer(idleDuration, onIdle);
	useActivityMonitor(idleDuration, onIdle, keepAliveInterval, keepAliveAction);

	useEffect(() => {
		console.log("isAuthenticated", isAuthenticated);
		const checkAuthentication = async () => {
			try {
				const authenticatedUser = await getAuthenticatedUser();
				if (authenticatedUser) {
					setIsAuthenticated(true);
				} else {
					setIsAuthenticated(false);
				}
			} catch (error) {
				setIsAuthenticated(false);
			} finally {
				setIsLoading(false); // Set loading to false when authentication check is done
			}
		};
		checkAuthentication(); //load user

		return () => {
		};
	}, [isAuthenticated]);


	useEffect(() => {
		// Load initial data from localStorage
		const email = localStorage.getItem("userEmail") || "";
		const groups = JSON.parse(localStorage.getItem("groups")) || [];
		const firstName = localStorage.getItem("firstName") || "";
		const lastName = localStorage.getItem("lastName") || "";
		setUserData({ email, groups, firstName, lastName });
	}, []);
	useInitializeBackendStatus(shouldInitializeBackendStatus);
	useRebootMonitor();

	return (
		<ScreenResolutionProvider>
			<ThemeProvider>
				<CssBaseline />
				<CompatibilityMatrixProvider>
					<BasicSnackbar />
					<ErrorBoundary>
						{isLoading ? (
							<PageSpinner />
						) : !isAuthenticated ? (
							<AuthPage hideSighUp={true} />
						) : (
							<>
								<UpdateWarningModal />
								<UpdateModal />
								<UpdateInstalledModal />
								<Router />
							</>
						)}
					</ErrorBoundary>
				</CompatibilityMatrixProvider>
			</ThemeProvider>
		</ScreenResolutionProvider>
	);
};